import BaseEntity from "%/dynamic-component-editor/classes/BaseEntity";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import { mergeArrayOfClassInstances } from "@bloglovin/vue-framework";

const { createProcessedComponentRegistry } = require("%/dynamic-component-editor/helpers/processed-component-registry");

const legacyElementComponentRegistry = createProcessedComponentRegistry();

export default class LegacyEntity extends BaseEntity {

  getElements() {
    // Subclasses declare `elements` two different ways:
    //   - static getter returning an array of components
    //     (LegacyGeneralEntity, LegacyLayoutEntity, …);
    //   - instance getter returning `{ Name: Component }` object
    //     (LegacyEvent, LegacyStory, …).
    // Read both shapes and normalise to an array before walking. The
    // previous version of this method only read the static and ran
    // forEach unconditionally, so any DC that resolved an instance-
    // getter Legacy entity (e.g. EventPage → LegacyEvent) crashed the
    // Elements panel with `Cannot read properties of undefined
    // (reading 'forEach')`.
    const raw = this.constructor.elements ?? this.elements;
    if (!raw) {
      return [];
    }
    const list = Array.isArray(raw) ? raw : Object.values(raw);
    return this.processElements(list);
  }

  processElements(elementComponents) {
    elementComponents.forEach(component => {
      if (!legacyElementComponentRegistry.shouldProcess(component)) {
        return;
      }
      let elementPanelSettings = {};
      let options = [];
      let optionBundles = [];
      let availableStates = [...Object.values(BaseOption.$states)];
      let optionsOrder = ["lock-options", "appearance", "animations"];
      let brandingType = null;

      const extendedComponent = component.extends;

      if (extendedComponent) {
        elementPanelSettings = {
          ...elementPanelSettings,
          ...extendedComponent.elementPanelSettings,
        };

        const allMixins = this.#getAllMixins(extendedComponent).reverse();
        allMixins.forEach(mixin => {
          elementPanelSettings = {
            ...elementPanelSettings,
            ...mixin.elementPanelSettings ?? {},
          };

          options = mergeArrayOfClassInstances(options, mixin.options ?? []);
          optionBundles = mergeArrayOfClassInstances(optionBundles, mixin.optionBundles ?? []);
          optionsOrder = mergeArrayOfClassInstances(optionsOrder, mixin.optionsOrder ?? []);
          availableStates = mergeArrayOfClassInstances(availableStates, mixin.availableStates ?? []);
          brandingType = brandingType ?? mixin.brandingType;
        });
      }


      const allMixins = this.#getAllMixins(component).reverse();
      allMixins.forEach(mixin => {
        elementPanelSettings = {
          ...elementPanelSettings,
          ...mixin.elementPanelSettings ?? {},
        };

        options = mergeArrayOfClassInstances(options, mixin.options ?? []);
        optionBundles = mergeArrayOfClassInstances(optionBundles, mixin.optionBundles ?? []);
        optionsOrder = mergeArrayOfClassInstances(optionsOrder, mixin.optionsOrder ?? []);
        availableStates = mergeArrayOfClassInstances(availableStates, mixin.availableStates ?? []);
        brandingType = brandingType ?? mixin.brandingType;
      });

      elementPanelSettings = {
        ...elementPanelSettings,
        ...component.elementPanelSettings ?? {},
      };

      options = mergeArrayOfClassInstances(options, component.options ?? []);
      optionBundles = mergeArrayOfClassInstances(optionBundles, component.optionBundles ?? []);
      optionsOrder = mergeArrayOfClassInstances(optionsOrder, component.optionsOrder ?? []);
      availableStates = mergeArrayOfClassInstances(availableStates, component.availableStates ?? []);
      brandingType = brandingType ?? component.brandingType;

      component.elementPanelSettings = elementPanelSettings;
      component.options = options;
      component.optionBundles = optionBundles;
      component.optionsOrder = optionsOrder;
      component.availableStates = availableStates;
      component.brandingType = brandingType;
    });

    return elementComponents;
  }

  #collectMixins(comp) {
    const allMixins = [];
    if (comp.mixins && Array.isArray(comp.mixins)) {
      comp.mixins.forEach(mixin => {
        this.#collectMixins(mixin);
        allMixins.push(mixin);
      });
    }

    return allMixins;
  }

  #getAllMixins(component) {
    const allMixins = [];
    allMixins.push(...this.#collectMixins(component));
    return allMixins;
  }
}
